import {
  closestCenter,
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import React from 'react';
import { PlusCircle } from 'react-bootstrap-icons';
import Divider from '../../../../../components/Divider/Divider';
import EditableLabel from '../../../../../components/Label/EditableLabel';
import CareMapprButton from '../../../../../components/Button/CareMapprButton';
import CaremapprConfirmation from '../../../../../components/Confirmation/CaremapprConfirmation';
import SortableItem from '../../../../../components/Sortable/SortableItem';
import moveSectionIcon from '../../../../../../assets/img/medicine-profile-icons/move-section.svg';
import {
  MedProfileStatus,
  MedProfileStatusEnum,
  MedSection,
} from '../../../hook/types';
import { useMedicationProfileCatalogue } from '../../../context/MedicationProfileCatalogueProvider';
import AddSection from './addSection/AddSection';
import AddImportantPatientInformationSection from './AddImportantPatientInformationSection';
import {
  IMPORTANT_PATIENT_INFORMATION,
  medSectionOptions,
} from '../../../constants';
import MedicationDragPreview from './sorting/MedicationDragPreview';
import ImportantPatientInformationContent from './ImportantPatientInformationContent';
import MedicationSortableList from './sorting/MedicationSortableList';
import {
  canDragSection,
  findMedicationBySortableId,
  getSectionSortableId,
  isMedicationSortableId,
  isSectionSortableId,
  moveMedicationBySortableIds,
  reorderDraggableSections,
} from './sorting/medicationDragUtils';

const SectionActions = ({
  canEditMedicationProfile,
  hasImportantPatientInformation,
  onAddSection,
  onAddImportantPatientInformation,
}: {
  canEditMedicationProfile: boolean;
  hasImportantPatientInformation: boolean;
  onAddSection: () => void;
  onAddImportantPatientInformation: () => void;
}) => {
  if (!canEditMedicationProfile) return null;

  return (
    <div className="d-flex gap-2" style={{ justifyContent: 'end' }}>
      <CareMapprButton
        endIcon={<PlusCircle size={14} />}
        label="Add section"
        labelClassName="add-section-button"
        hoverWithColor={false}
        onClick={onAddSection}
      />
      {!hasImportantPatientInformation ? (
        <CareMapprButton
          endIcon={<PlusCircle size={14} />}
          label="Add important patient info"
          labelClassName="add-section-button"
          hoverWithColor={false}
          onClick={onAddImportantPatientInformation}
        />
      ) : null}
    </div>
  );
};

// TODO fix these any
// @ts-ignore
const MedicationInfos = ({
  canEditMedicationProfile,
  onAddSection,
  onEditSection,
  onRemoveSection,
  onRemoveMedicationFromSection,
  onEditMedicationToSection,
  medicationProfileForm,
  profileStatus,
}: {
  canEditMedicationProfile: boolean;
  onAddSection: (
    name: string,
    options?: { position?: 'top'; sectionText?: string; medName?: string },
  ) => void;
  onEditSection: (name: string, indexToUpdate: number) => void;
  onRemoveSection: (indexToRemove: number) => void;
  onRemoveMedicationFromSection: (medId: number, sectionName: string) => void;
  onEditMedicationToSection: (data: any, targetSection: string) => void;
  medicationProfileForm: any;
  profileStatus?: MedProfileStatus;
}) => {
  const {
    medSections: sectionsData,
    setMedSections,
    isLoadingCatalogue,
  } = useMedicationProfileCatalogue();
  const [openAddSection, setOpenAddSection] = React.useState(false);
  const [
    openAddImportantPatientInformation,
    setOpenAddImportantPatientInformation,
  ] = React.useState(false);
  const [deleteSectionIndex, setDeleteSectionIndex] = React.useState<
    number | null
  >(null);
  const [isSectionDeleteConfirmOpen, setIsSectionDeleteConfirmOpen] =
    React.useState(false);
  const [deleteTarget, setDeleteTarget] = React.useState<{
    medId: number;
    sectionName: string;
  } | null>(null);
  const [ipiEditing, setIpiEditing] = React.useState(false);
  const [ipiDraft, setIpiDraft] = React.useState('');
  const [ipiTitleDraft, setIpiTitleDraft] = React.useState('');
  const [activeDragType, setActiveDragType] = React.useState<
    'section' | 'medication' | null
  >(null);
  const [activeDragId, setActiveDragId] = React.useState<string | null>(null);
  const sections = React.useMemo(
    () => (Array.isArray(sectionsData) ? sectionsData : []),
    [sectionsData],
  );
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 6,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );
  const canDragAndDrop =
    canEditMedicationProfile &&
    profileStatus !== MedProfileStatusEnum.FINALISED &&
    profileStatus !== MedProfileStatusEnum.ENTERED_IN_ERROR;

  const collisionDetection = React.useCallback(
    (args: Parameters<typeof closestCenter>[0]) => {
      if (!isMedicationSortableId(String(args.active.id))) {
        return closestCenter(args);
      }

      return closestCenter({
        ...args,
        droppableContainers: args.droppableContainers.filter(
          (container) => !isSectionSortableId(String(container.id)),
        ),
      });
    },
    [],
  );

  const addSectionOptions = React.useMemo(() => {
    const existing = new Set(
      sections
        .map((s) => s?.medSectionBreak)
        .filter(
          (v): v is string => typeof v === 'string' && v.trim().length > 0,
        ),
    );
    return medSectionOptions.filter((opt) => !existing.has(opt.value));
  }, [sections]);

  const hasImportantPatientInformation = React.useMemo(
    () =>
      sections.some(
        (s) => s?.medSectionBreak === IMPORTANT_PATIENT_INFORMATION,
      ),
    [sections],
  );

  const sectionSortableItems = React.useMemo(
    () =>
      sections
        .map((section, index) => ({ section, index }))
        .filter(({ section }) => canDragSection(section))
        .map(({ section, index }) => getSectionSortableId(section, index)),
    [sections],
  );

  const activeMedication = React.useMemo(() => {
    if (!activeDragId || !isMedicationSortableId(activeDragId)) return null;
    return findMedicationBySortableId(sections, activeDragId);
  }, [activeDragId, sections]);

  const activeMedicationSourceSectionName = React.useMemo(() => {
    if (!activeMedication) return null;
    return sections[activeMedication.sectionIndex]?.medSectionBreak ?? null;
  }, [activeMedication, sections]);

  const resetActiveDragState = React.useCallback(() => {
    setActiveDragType(null);
    setActiveDragId(null);
  }, []);

  const onCloseAddSection = () => {
    setOpenAddSection(false);
  };

  const onCloseAddImportantPatientInformation = () => {
    setOpenAddImportantPatientInformation(false);
  };

  const onSaveEditSection = (name: string, indexToUpdate: number) => {
    onEditSection(name, indexToUpdate);
    onCloseAddSection();
  };

  const handleRequestDelete = React.useCallback(
    (medId: number, sectionName: string) => {
      setDeleteTarget({ medId, sectionName });
    },
    [],
  );

  const handleCancelDelete = React.useCallback(() => {
    setDeleteTarget(null);
  }, []);

  const handleConfirmDelete = React.useCallback(
    (medId?: number, sectionName?: string) => {
      if (typeof medId === 'number' && typeof sectionName === 'string') {
        onRemoveMedicationFromSection(medId, sectionName);
        return;
      }
      if (!deleteTarget) return;
      onRemoveMedicationFromSection(
        deleteTarget.medId,
        deleteTarget.sectionName,
      );
      setDeleteTarget(null);
    },
    [deleteTarget, onRemoveMedicationFromSection],
  );

  const onDragEnd = (event: DragEndEvent) => {
    if (!canEditMedicationProfile) return;

    const { active, over } = event;
    const activeId = String(active.id);
    const overId = over ? String(over.id) : '';

    if (!over || activeId === overId) {
      resetActiveDragState();
      return;
    }

    if (isSectionSortableId(activeId)) {
      const reorderedSections = reorderDraggableSections(
        sections,
        sectionSortableItems,
        activeId,
        overId,
      );
      if (reorderedSections) {
        setMedSections(reorderedSections);
      }
      resetActiveDragState();
      return;
    }

    if (isMedicationSortableId(activeId)) {
      const nextSections = moveMedicationBySortableIds(
        sections,
        activeId,
        overId,
      );
      if (nextSections) {
        setMedSections(nextSections);
      }
    }

    resetActiveDragState();
  };

  const onDragStart = (event: DragStartEvent) => {
    if (!canEditMedicationProfile) return;
    const activeId = String(event.active.id);
    setActiveDragId(activeId);
    if (isSectionSortableId(activeId)) {
      setActiveDragType('section');
      return;
    }
    if (isMedicationSortableId(activeId)) {
      setActiveDragType('medication');
      return;
    }
    setActiveDragType(null);
  };

  const buildSectionDeleteConfirmText = (): string => {
    const header = 'Are you sure you want to delete this section?';
    return `${header}\n\nDeleting this section will also delete any medications in it.`;
  };

  const sectionToDelete =
    typeof deleteSectionIndex === 'number'
      ? sections[deleteSectionIndex]
      : null;

  return isLoadingCatalogue ? null : (
    <div className="d-flex flex-column">
      <CaremapprConfirmation
        open={isSectionDeleteConfirmOpen}
        text=""
        confirmButtonText="Delete"
        cancelButtonText="Cancel"
        onConfirm={() => {
          if (typeof deleteSectionIndex !== 'number') return;
          onRemoveSection(deleteSectionIndex);
        }}
        onClose={() => {
          setIsSectionDeleteConfirmOpen(false);
          setDeleteSectionIndex(null);
        }}
        options={{
          html: `<div style="text-align:left; white-space:pre-wrap;">${
            sectionToDelete ? buildSectionDeleteConfirmText() : ''
          }</div>`,
        }}
      />
      <AddSection
        open={openAddSection}
        onClose={onCloseAddSection}
        onAddSection={onAddSection}
        options={addSectionOptions}
      />
      <AddImportantPatientInformationSection
        open={openAddImportantPatientInformation}
        onClose={onCloseAddImportantPatientInformation}
        onAddSection={onAddSection}
      />
      <SectionActions
        canEditMedicationProfile={canEditMedicationProfile}
        hasImportantPatientInformation={hasImportantPatientInformation}
        onAddSection={() => setOpenAddSection(true)}
        onAddImportantPatientInformation={() =>
          setOpenAddImportantPatientInformation(true)
        }
      />
      {sections.length === 0 ? null : (
        <DndContext
          sensors={sensors}
          collisionDetection={collisionDetection}
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
          onDragCancel={resetActiveDragState}
        >
          <SortableContext
            items={sectionSortableItems}
            strategy={verticalListSortingStrategy}
          >
            {sections.map((section: MedSection, index: number) => {
              const sectionId = getSectionSortableId(section, index);
              const showSectionDragHandle =
                canDragAndDrop && canDragSection(section);

              return (
                <SortableItem
                  key={sectionId}
                  id={sectionId}
                  disabled={!showSectionDragHandle}
                  handle={
                    showSectionDragHandle
                      ? ({ attributes, listeners, disabled }) => (
                          <button
                            type="button"
                            aria-label="Drag section"
                            style={{
                              border: 'none',
                              background: 'transparent',
                              padding: '28px 0 0',
                              cursor: disabled ? 'default' : 'grab',
                              color: '#5b6366',
                            }}
                            {...attributes}
                            {...listeners}
                            disabled={disabled}
                          >
                            <img
                              src={moveSectionIcon}
                              alt="drag sort"
                              style={{
                                width: 16,
                                height: 16,
                                opacity: disabled ? 0.5 : 1,
                                display: 'block',
                              }}
                            />
                          </button>
                        )
                      : undefined
                  }
                >
                  <div className="d-flex justify-content-between">
                    <EditableLabel
                      id={section.medSectionBreak + '' + index}
                      key={section.medSectionBreak + '' + index}
                      className="font-lg my-3"
                      label={section.medSectionBreak}
                      onSave={(newValue: string) =>
                        onSaveEditSection(newValue, index)
                      }
                      canEdit={
                        canEditMedicationProfile &&
                        section.medSectionBreak != IMPORTANT_PATIENT_INFORMATION
                      }
                      canDelete={canEditMedicationProfile}
                      onRequestDelete={() => {
                        setDeleteSectionIndex(index);
                        setIsSectionDeleteConfirmOpen(true);
                      }}
                    />
                  </div>
                  <Divider />
                  {activeDragType === 'section' ? null : (
                    <>
                      <ImportantPatientInformationContent
                        section={section}
                        canEditMedicationProfile={canEditMedicationProfile}
                        ipiEditing={ipiEditing}
                        ipiDraft={ipiDraft}
                        ipiTitleDraft={ipiTitleDraft}
                        setIpiEditing={setIpiEditing}
                        setIpiDraft={setIpiDraft}
                        setIpiTitleDraft={setIpiTitleDraft}
                        onEditMedicationToSection={onEditMedicationToSection}
                      />
                      {section.medSectionBreak !==
                      IMPORTANT_PATIENT_INFORMATION ? (
                        <MedicationSortableList
                          section={section}
                          sectionIndex={index}
                          canEditMedicationProfile={canEditMedicationProfile}
                          canDragAndDrop={canDragAndDrop}
                          activeDragType={activeDragType}
                          activeDragId={activeDragId}
                          activeMedicationSourceSectionName={
                            activeMedicationSourceSectionName
                          }
                          profileStatus={profileStatus}
                          deleteTarget={deleteTarget}
                          onRequestDelete={handleRequestDelete}
                          onCancelDelete={handleCancelDelete}
                          onConfirmDelete={handleConfirmDelete}
                          onEditMedicationToSection={onEditMedicationToSection}
                          medicationProfileForm={medicationProfileForm}
                        />
                      ) : null}
                    </>
                  )}
                </SortableItem>
              );
            })}
          </SortableContext>
          <DragOverlay dropAnimation={null}>
            {activeDragType === 'medication' && activeMedication ? (
              <MedicationDragPreview medication={activeMedication.medication} />
            ) : null}
          </DragOverlay>
        </DndContext>
      )}
    </div>
  );
};

export default MedicationInfos;
